/**
  @module ember-data
*/

import Adapter from "ember-data/system/adapter";
import { AdapterError, InvalidError, TimeoutError, AbortError } from "ember-data/adapters/errors";
import { MapWithDefault } from "ember-data/system/map";

import { create, keysFunc } from "ember-data/system/object-polyfills";

import BuildURLMixin from "ember-data/adapters/build-url-mixin";

var get = Ember.get;
var set = Ember.set;
var forEach = Ember.ArrayPolyfills.forEach; /**
                                              The REST adapter allows your store to communicate with an HTTP server by
                                              transmitting JSON via XHR. Most Ember.js apps that consume a JSON API
                                              should use the REST adapter.
                                            
                                              This adapter is designed around the idea that the JSON exchanged with
                                              the server should be conventional.
                                            
                                              ## JSON Structure
                                            
                                              The REST adapter expects the JSON returned from your server to follow
                                              these conventions.
                                            
                                              ### Object Root
                                            
                                              The JSON payload should be an object that contains the record inside a
                                              root property. For example, in response to a `GET` request for
                                              `/posts/1`, the JSON should look like this:
                                            
                                              ```js
                                              {
                                                "post": {
                                                  "id": 1,
                                                  "title": "I'm Running to Reform the W3C's Tag",
                                                  "author": "Yehuda Katz"
                                                }
                                              }
                                              ```
                                            
                                              Similarly, in response to a `GET` request for `/posts`, the JSON should
                                              look like this:
                                            
                                              ```js
                                              {
                                                "posts": [
                                                  {
                                                    "id": 1,
                                                    "title": "I'm Running to Reform the W3C's Tag",
                                                    "author": "Yehuda Katz"
                                                  },
                                                  {
                                                    "id": 2,
                                                    "title": "Rails is omakase",
                                                    "author": "D2H"
                                                  }
                                                ]
                                              }
                                              ```
                                            
                                              ### Conventional Names
                                            
                                              Attribute names in your JSON payload should be the camelCased versions of
                                              the attributes in your Ember.js models.
                                            
                                              For example, if you have a `Person` model:
                                            
                                              ```app/models/person.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.Model.extend({
                                                firstName: DS.attr('string'),
                                                lastName: DS.attr('string'),
                                                occupation: DS.attr('string')
                                              });
                                              ```
                                            
                                              The JSON returned should look like this:
                                            
                                              ```js
                                              {
                                                "person": {
                                                  "id": 5,
                                                  "firstName": "Barack",
                                                  "lastName": "Obama",
                                                  "occupation": "President"
                                                }
                                              }
                                              ```
                                            
                                              ## Customization
                                            
                                              ### Endpoint path customization
                                            
                                              Endpoint paths can be prefixed with a `namespace` by setting the namespace
                                              property on the adapter:
                                            
                                              ```app/adapters/application.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.RESTAdapter.extend({
                                                namespace: 'api/1'
                                              });
                                              ```
                                              Requests for `App.Person` would now target `/api/1/people/1`.
                                            
                                              ### Host customization
                                            
                                              An adapter can target other hosts by setting the `host` property.
                                            
                                              ```app/adapters/application.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.RESTAdapter.extend({
                                                host: 'https://api.example.com'
                                              });
                                              ```
                                            
                                              ### Headers customization
                                            
                                              Some APIs require HTTP headers, e.g. to provide an API key. Arbitrary
                                              headers can be set as key/value pairs on the `RESTAdapter`'s `headers`
                                              object and Ember Data will send them along with each ajax request.
                                            
                                            
                                              ```app/adapters/application.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.RESTAdapter.extend({
                                                headers: {
                                                  "API_KEY": "secret key",
                                                  "ANOTHER_HEADER": "Some header value"
                                                }
                                              });
                                              ```
                                            
                                              `headers` can also be used as a computed property to support dynamic
                                              headers. In the example below, the `session` object has been
                                              injected into an adapter by Ember's container.
                                            
                                              ```app/adapters/application.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.RESTAdapter.extend({
                                                headers: function() {
                                                  return {
                                                    "API_KEY": this.get("session.authToken"),
                                                    "ANOTHER_HEADER": "Some header value"
                                                  };
                                                }.property("session.authToken")
                                              });
                                              ```
                                            
                                              In some cases, your dynamic headers may require data from some
                                              object outside of Ember's observer system (for example
                                              `document.cookie`). You can use the
                                              [volatile](/api/classes/Ember.ComputedProperty.html#method_volatile)
                                              function to set the property into a non-cached mode causing the headers to
                                              be recomputed with every request.
                                            
                                              ```app/adapters/application.js
                                              import DS from 'ember-data';
                                            
                                              export default DS.RESTAdapter.extend({
                                                headers: function() {
                                                  return {
                                                    "API_KEY": Ember.get(document.cookie.match(/apiKey\=([^;]*)/), "1"),
                                                    "ANOTHER_HEADER": "Some header value"
                                                  };
                                                }.property().volatile()
                                              });
                                              ```
                                            
                                              @class RESTAdapter
                                              @constructor
                                              @namespace DS
                                              @extends DS.Adapter
                                              @uses DS.BuildURLMixin
                                            */
var RestAdapter = Adapter.extend(BuildURLMixin, {
  defaultSerializer: "-rest",

  /**
    By default, the RESTAdapter will send the query params sorted alphabetically to the
    server.
     For example:
     ```js
      store.query('posts', { sort: 'price', category: 'pets' });
    ```
     will generate a requests like this `/posts?category=pets&sort=price`, even if the
    parameters were specified in a different order.
     That way the generated URL will be deterministic and that simplifies caching mechanisms
    in the backend.
     Setting `sortQueryParams` to a falsey value will respect the original order.
     In case you want to sort the query parameters with a different criteria, set
    `sortQueryParams` to your custom sort function.
     ```app/adapters/application.js
    import DS from 'ember-data';
     export default DS.RESTAdapter.extend({
      sortQueryParams: function(params) {
        var sortedKeys = Object.keys(params).sort().reverse();
        var len = sortedKeys.length, newParams = {};
         for (var i = 0; i < len; i++) {
          newParams[sortedKeys[i]] = params[sortedKeys[i]];
        }
        return newParams;
      }
    });
    ```
     @method sortQueryParams
    @param {Object} obj
    @return {Object}
  */
  sortQueryParams: function (obj) {
    var keys = keysFunc(obj);
    var len = keys.length;
    if (len < 2) {
      return obj;
    }
    var newQueryParams = {};
    var sortedKeys = keys.sort();

    for (var i = 0; i < len; i++) {
      newQueryParams[sortedKeys[i]] = obj[sortedKeys[i]];
    }
    return newQueryParams;
  },

  /**
    By default the RESTAdapter will send each find request coming from a `store.find`
    or from accessing a relationship separately to the server. If your server supports passing
    ids as a query string, you can set coalesceFindRequests to true to coalesce all find requests
    within a single runloop.
     For example, if you have an initial payload of:
     ```javascript
    {
      post: {
        id: 1,
        comments: [1, 2]
      }
    }
    ```
     By default calling `post.get('comments')` will trigger the following requests(assuming the
    comments haven't been loaded before):
     ```
    GET /comments/1
    GET /comments/2
    ```
     If you set coalesceFindRequests to `true` it will instead trigger the following request:
     ```
    GET /comments?ids[]=1&ids[]=2
    ```
     Setting coalesceFindRequests to `true` also works for `store.find` requests and `belongsTo`
    relationships accessed within the same runloop. If you set `coalesceFindRequests: true`
     ```javascript
    store.findRecord('comment', 1);
    store.findRecord('comment', 2);
    ```
     will also send a request to: `GET /comments?ids[]=1&ids[]=2`
     Note: Requests coalescing rely on URL building strategy. So if you override `buildURL` in your app
    `groupRecordsForFindMany` more likely should be overridden as well in order for coalescing to work.
     @property coalesceFindRequests
    @type {boolean}
  */
  coalesceFindRequests: false,

  /**
    Endpoint paths can be prefixed with a `namespace` by setting the namespace
    property on the adapter:
     ```app/adapters/application.js
    import DS from 'ember-data';
     export default DS.RESTAdapter.extend({
      namespace: 'api/1'
    });
    ```
     Requests for `App.Post` would now target `/api/1/post/`.
     @property namespace
    @type {String}
  */

  /**
    An adapter can target other hosts by setting the `host` property.
     ```app/adapters/application.js
    import DS from 'ember-data';
     export default DS.RESTAdapter.extend({
      host: 'https://api.example.com'
    });
    ```
     Requests for `App.Post` would now target `https://api.example.com/post/`.
     @property host
    @type {String}
  */

  /**
    Some APIs require HTTP headers, e.g. to provide an API
    key. Arbitrary headers can be set as key/value pairs on the
    `RESTAdapter`'s `headers` object and Ember Data will send them
    along with each ajax request. For dynamic headers see [headers
    customization](/api/data/classes/DS.RESTAdapter.html#toc_headers-customization).
     ```app/adapters/application.js
    import DS from 'ember-data';
     export default DS.RESTAdapter.extend({
      headers: {
        "API_KEY": "secret key",
        "ANOTHER_HEADER": "Some header value"
      }
    });
    ```
     @property headers
    @type {Object}
   */

  /**
    @method find
    @param {DS.Store} store
    @param {DS.Model} type
    @param {String} id
    @param {DS.Snapshot} snapshot
    @return {Promise} promise
    @deprecated Use [findRecord](#method_findRecord) instead
  */
  find: function (store, type, id, snapshot) {
    Ember.deprecate("RestAdapter#find has been deprecated and renamed to `findRecord`.", false, {
      id: "ds.adapter.find-renamed-to-find-record",
      until: "2.0.0"
    });
    return this.ajax(this.buildURL(type.modelName, id, snapshot, "find"), "GET");
  },

  /**
    Called by the store in order to fetch the JSON for a given
    type and ID.
     The `findRecord` method makes an Ajax request to a URL computed by
    `buildURL`, and returns a promise for the resulting payload.
     This method performs an HTTP `GET` request with the id provided as part of the query string.
     @method findRecord
    @param {DS.Store} store
    @param {DS.Model} type
    @param {String} id
    @param {DS.Snapshot} snapshot
    @return {Promise} promise
  */
  findRecord: function (store, type, id, snapshot) {
    var find = RestAdapter.prototype.find;
    if (find !== this.find) {
      Ember.deprecate("RestAdapter#find has been deprecated and renamed to `findRecord`.", false, {
        id: "ds.adapter.find-renamed-to-find-record",
        until: "2.0.0"
      });
      return this.find(store, type, id, snapshot);
    }
    return this.ajax(this.buildURL(type.modelName, id, snapshot, "findRecord"), "GET");
  },

  /**
    Called by the store in order to fetch a JSON array for all
    of the records for a given type.
     The `findAll` method makes an Ajax (HTTP GET) request to a URL computed by `buildURL`, and returns a
    promise for the resulting payload.
     @private
    @method findAll
    @param {DS.Store} store
    @param {DS.Model} type
    @param {String} sinceToken
    @return {Promise} promise
  */
  findAll: function (store, type, sinceToken) {
    var query, url;

    if (sinceToken) {
      query = { since: sinceToken };
    }

    url = this.buildURL(type.modelName, null, null, "findAll");

    return this.ajax(url, "GET", { data: query });
  },

  /**
    Called by the store in order to fetch a JSON array for
    the records that match a particular query.
     The `findQuery` method makes an Ajax (HTTP GET) request to a URL computed by `buildURL`, and returns a
    promise for the resulting payload.
     The `query` argument is a simple JavaScript object that will be passed directly
    to the server as parameters.
     @private
    @method findQuery
    @param {DS.Store} store
    @param {DS.Model} type
    @param {Object} query
    @return {Promise} promise
    @deprecated Use [query](#method_query) instead
  */
  findQuery: function (store, type, query) {
    Ember.deprecate("RestAdapter#findQuery has been deprecated and renamed to `query`.", false, {
      id: "ds.adapter.find-query-renamed-to-query",
      until: "2.0.0"
    });
    var url = this.buildURL(type.modelName, null, null, "findQuery", query);

    if (this.sortQueryParams) {
      query = this.sortQueryParams(query);
    }

    return this.ajax(url, "GET", { data: query });
  },

  /**
    Called by the store in order to fetch a JSON array for
    the records that match a particular query.
     The `findQuery` method makes an Ajax (HTTP GET) request to a URL computed by `buildURL`, and returns a
    promise for the resulting payload.
     The `query` argument is a simple JavaScript object that will be passed directly
    to the server as parameters.
     @private
    @method query
    @param {DS.Store} store
    @param {DS.Model} type
    @param {Object} query
    @return {Promise} promise
  */
  query: function (store, type, query) {
    var findQuery = RestAdapter.prototype.findQuery;
    if (findQuery !== this.findQuery) {
      Ember.deprecate("RestAdapter#findQuery has been deprecated and renamed to `query`.", false, {
        id: "ds.adapter.find-query-renamed-to-query",
        until: "2.0.0"
      });
      return this.findQuery(store, type, query);
    }

    var url = this.buildURL(type.modelName, null, null, "query", query);

    if (this.sortQueryParams) {
      query = this.sortQueryParams(query);
    }

    return this.ajax(url, "GET", { data: query });
  },

  /**
    Called by the store in order to fetch a JSON object for
    the record that matches a particular query.
     The `queryRecord` method makes an Ajax (HTTP GET) request to a URL
    computed by `buildURL`, and returns a promise for the resulting
    payload.
     The `query` argument is a simple JavaScript object that will be passed directly
    to the server as parameters.
     @private
    @method queryRecord
    @param {DS.Store} store
    @param {DS.Model} type
    @param {Object} query
    @return {Promise} promise
  */
  queryRecord: function (store, type, query) {
    var url = this.buildURL(type.modelName, null, null, "queryRecord", query);

    if (this.sortQueryParams) {
      query = this.sortQueryParams(query);
    }

    return this.ajax(url, "GET", { data: query });
  },

  /**
    Called by the store in order to fetch several records together if `coalesceFindRequests` is true
     For example, if the original payload looks like:
     ```js
    {
      "id": 1,
      "title": "Rails is omakase",
      "comments": [ 1, 2, 3 ]
    }
    ```
     The IDs will be passed as a URL-encoded Array of IDs, in this form:
     ```
    ids[]=1&ids[]=2&ids[]=3
    ```
     Many servers, such as Rails and PHP, will automatically convert this URL-encoded array
    into an Array for you on the server-side. If you want to encode the
    IDs, differently, just override this (one-line) method.
     The `findMany` method makes an Ajax (HTTP GET) request to a URL computed by `buildURL`, and returns a
    promise for the resulting payload.
     @method findMany
    @param {DS.Store} store
    @param {DS.Model} type
    @param {Array} ids
    @param {Array} snapshots
    @return {Promise} promise
  */
  findMany: function (store, type, ids, snapshots) {
    var url = this.buildURL(type.modelName, ids, snapshots, "findMany");
    return this.ajax(url, "GET", { data: { ids: ids } });
  },

  /**
    Called by the store in order to fetch a JSON array for
    the unloaded records in a has-many relationship that were originally
    specified as a URL (inside of `links`).
     For example, if your original payload looks like this:
     ```js
    {
      "post": {
        "id": 1,
        "title": "Rails is omakase",
        "links": { "comments": "/posts/1/comments" }
      }
    }
    ```
     This method will be called with the parent record and `/posts/1/comments`.
     The `findHasMany` method will make an Ajax (HTTP GET) request to the originally specified URL.
     @method findHasMany
    @param {DS.Store} store
    @param {DS.Snapshot} snapshot
    @param {String} url
    @return {Promise} promise
  */
  findHasMany: function (store, snapshot, url, relationship) {
    var id = snapshot.id;
    var type = snapshot.modelName;

    url = this.urlPrefix(url, this.buildURL(type, id, null, "findHasMany"));

    return this.ajax(url, "GET");
  },

  /**
    Called by the store in order to fetch a JSON array for
    the unloaded records in a belongs-to relationship that were originally
    specified as a URL (inside of `links`).
     For example, if your original payload looks like this:
     ```js
    {
      "person": {
        "id": 1,
        "name": "Tom Dale",
        "links": { "group": "/people/1/group" }
      }
    }
    ```
     This method will be called with the parent record and `/people/1/group`.
     The `findBelongsTo` method will make an Ajax (HTTP GET) request to the originally specified URL.
     @method findBelongsTo
    @param {DS.Store} store
    @param {DS.Snapshot} snapshot
    @param {String} url
    @return {Promise} promise
  */
  findBelongsTo: function (store, snapshot, url, relationship) {
    var id = snapshot.id;
    var type = snapshot.modelName;

    url = this.urlPrefix(url, this.buildURL(type, id, null, "findBelongsTo"));
    return this.ajax(url, "GET");
  },

  /**
    Called by the store when a newly created record is
    saved via the `save` method on a model record instance.
     The `createRecord` method serializes the record and makes an Ajax (HTTP POST) request
    to a URL computed by `buildURL`.
     See `serialize` for information on how to customize the serialized form
    of a record.
     @method createRecord
    @param {DS.Store} store
    @param {DS.Model} type
    @param {DS.Snapshot} snapshot
    @return {Promise} promise
  */
  createRecord: function (store, type, snapshot) {
    var data = {};
    var serializer = store.serializerFor(type.modelName);
    var url = this.buildURL(type.modelName, null, snapshot, "createRecord");

    serializer.serializeIntoHash(data, type, snapshot, { includeId: true });

    return this.ajax(url, "POST", { data: data });
  },

  /**
    Called by the store when an existing record is saved
    via the `save` method on a model record instance.
     The `updateRecord` method serializes the record and makes an Ajax (HTTP PUT) request
    to a URL computed by `buildURL`.
     See `serialize` for information on how to customize the serialized form
    of a record.
     @method updateRecord
    @param {DS.Store} store
    @param {DS.Model} type
    @param {DS.Snapshot} snapshot
    @return {Promise} promise
  */
  updateRecord: function (store, type, snapshot) {
    var data = {};
    var serializer = store.serializerFor(type.modelName);

    serializer.serializeIntoHash(data, type, snapshot);

    var id = snapshot.id;
    var url = this.buildURL(type.modelName, id, snapshot, "updateRecord");

    return this.ajax(url, "PUT", { data: data });
  },

  /**
    Called by the store when a record is deleted.
     The `deleteRecord` method  makes an Ajax (HTTP DELETE) request to a URL computed by `buildURL`.
     @method deleteRecord
    @param {DS.Store} store
    @param {DS.Model} type
    @param {DS.Snapshot} snapshot
    @return {Promise} promise
  */
  deleteRecord: function (store, type, snapshot) {
    var id = snapshot.id;

    return this.ajax(this.buildURL(type.modelName, id, snapshot, "deleteRecord"), "DELETE");
  },

  _stripIDFromURL: function (store, snapshot) {
    var url = this.buildURL(snapshot.modelName, snapshot.id, snapshot);

    var expandedURL = url.split("/");
    //Case when the url is of the format ...something/:id
    var lastSegment = expandedURL[expandedURL.length - 1];
    var id = snapshot.id;
    if (lastSegment === id) {
      expandedURL[expandedURL.length - 1] = "";
    } else if (endsWith(lastSegment, "?id=" + id)) {
      //Case when the url is of the format ...something?id=:id
      expandedURL[expandedURL.length - 1] = lastSegment.substring(0, lastSegment.length - id.length - 1);
    }

    return expandedURL.join("/");
  },

  // http://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers
  maxURLLength: 2048,

  /**
    Organize records into groups, each of which is to be passed to separate
    calls to `findMany`.
     This implementation groups together records that have the same base URL but
    differing ids. For example `/comments/1` and `/comments/2` will be grouped together
    because we know findMany can coalesce them together as `/comments?ids[]=1&ids[]=2`
     It also supports urls where ids are passed as a query param, such as `/comments?id=1`
    but not those where there is more than 1 query param such as `/comments?id=2&name=David`
    Currently only the query param of `id` is supported. If you need to support others, please
    override this or the `_stripIDFromURL` method.
     It does not group records that have differing base urls, such as for example: `/posts/1/comments/2`
    and `/posts/2/comments/3`
     @method groupRecordsForFindMany
    @param {DS.Store} store
    @param {Array} snapshots
    @return {Array}  an array of arrays of records, each of which is to be
                      loaded separately by `findMany`.
  */
  groupRecordsForFindMany: function (store, snapshots) {
    var groups = MapWithDefault.create({ defaultValue: function () {
        return [];
      } });
    var adapter = this;
    var maxURLLength = this.maxURLLength;

    forEach.call(snapshots, function (snapshot) {
      var baseUrl = adapter._stripIDFromURL(store, snapshot);
      groups.get(baseUrl).push(snapshot);
    });

    function splitGroupToFitInUrl(group, maxURLLength, paramNameLength) {
      var baseUrl = adapter._stripIDFromURL(store, group[0]);
      var idsSize = 0;
      var splitGroups = [[]];

      forEach.call(group, function (snapshot) {
        var additionalLength = encodeURIComponent(snapshot.id).length + paramNameLength;
        if (baseUrl.length + idsSize + additionalLength >= maxURLLength) {
          idsSize = 0;
          splitGroups.push([]);
        }

        idsSize += additionalLength;

        var lastGroupIndex = splitGroups.length - 1;
        splitGroups[lastGroupIndex].push(snapshot);
      });

      return splitGroups;
    }

    var groupsArray = [];
    groups.forEach(function (group, key) {
      var paramNameLength = "&ids%5B%5D=".length;
      var splitGroups = splitGroupToFitInUrl(group, maxURLLength, paramNameLength);

      forEach.call(splitGroups, function (splitGroup) {
        groupsArray.push(splitGroup);
      });
    });

    return groupsArray;
  },

  /**
    Takes an ajax response, and returns an error payload.
     @method ajaxError
    @deprecated Use [handleResponse](#method_handleResponse) instead
    @param  {Object} jqXHR
    @param  {Object} responseText
    @param  {Object} errorThrown
    @return {Object} jqXHR
  */

  /**
    Takes an ajax response, and returns the json payload.
     @method ajaxSuccess
    @deprecated Use [handleResponse](#method_handleResponse) instead
    @param  {Object} jqXHR
    @param  {Object} jsonPayload
    @return {Object} jsonPayload
  */

  /**
    Takes an ajax response, and returns the json payload or an error.
     By default this hook just returns the json payload passed to it.
    You might want to override it in two cases:
     1. Your API might return useful results in the response headers.
    Response headers are passed in as the second argument.
     2. Your API might return errors as successful responses with status code
    200 and an Errors text or object. You can return a `DS.InvalidError` or a
    `DS.AdapterError` (or a sub class) from this hook and it will automatically
    reject the promise and put your record into the invalid or error state.
     Returning a `DS.InvalidError` from this method will cause the
    record to transition into the `invalid` state and make the
    `errors` object available on the record. When returning an
    `DS.InvalidError` the store will attempt to normalize the error data
    returned from the server using the serializer's `extractErrors`
    method.
     @method handleResponse
    @param  {Number} status
    @param  {Object} headers
    @param  {Object} payload
    @return {Object | DS.AdapterError} response
  */
  handleResponse: function (status, headers, payload) {
    if (this.isSuccess(status, headers, payload)) {
      return payload;
    } else if (this.isInvalid(status, headers, payload)) {
      return new InvalidError(payload.errors);
    }

    var errors = this.normalizeErrorResponse(status, headers, payload);

    return new AdapterError(errors);
  },

  /**
    Default `handleResponse` implementation uses this hook to decide if the
    response is a success.
     @method isSuccess
    @param  {Number} status
    @param  {Object} headers
    @param  {Object} payload
    @return {Boolean}
  */
  isSuccess: function (status, headers, payload) {
    return status >= 200 && status < 300 || status === 304;
  },

  /**
    Default `handleResponse` implementation uses this hook to decide if the
    response is a an invalid error.
     @method isInvalid
    @param  {Number} status
    @param  {Object} headers
    @param  {Object} payload
    @return {Boolean}
  */
  isInvalid: function (status, headers, payload) {
    return status === 422;
  },

  /**
    Takes a URL, an HTTP method and a hash of data, and makes an
    HTTP request.
     When the server responds with a payload, Ember Data will call into `extractSingle`
    or `extractArray` (depending on whether the original query was for one record or
    many records).
     By default, `ajax` method has the following behavior:
     * It sets the response `dataType` to `"json"`
    * If the HTTP method is not `"GET"`, it sets the `Content-Type` to be
      `application/json; charset=utf-8`
    * If the HTTP method is not `"GET"`, it stringifies the data passed in. The
      data is the serialized record in the case of a save.
    * Registers success and failure handlers.
     @method ajax
    @private
    @param {String} url
    @param {String} type The request type GET, POST, PUT, DELETE etc.
    @param {Object} options
    @return {Promise} promise
  */
  ajax: function (url, type, options) {
    var adapter = this;

    return new Ember.RSVP.Promise(function (resolve, reject) {
      var hash = adapter.ajaxOptions(url, type, options);

      hash.success = function (payload, textStatus, jqXHR) {
        var response = undefined;

        if (adapter.ajaxSuccess) {
          Ember.deprecate("`ajaxSuccess` has been deprecated. Use `isSuccess`, `isInvalid` or `handleResponse` instead.", false, {
            id: "ds.adapter.ajax-success-deprecated",
            until: "2.0.0"
          });
          response = adapter.ajaxSuccess(jqXHR, payload);
        }

        if (!(response instanceof AdapterError)) {
          response = adapter.handleResponse(jqXHR.status, parseResponseHeaders(jqXHR.getAllResponseHeaders()), response || payload);
        }

        if (response instanceof AdapterError) {
          Ember.run(null, reject, response);
        } else {
          Ember.run(null, resolve, response);
        }
      };

      hash.error = function (jqXHR, textStatus, errorThrown) {
        var error = undefined;

        if (adapter.ajaxError) {
          Ember.deprecate("`ajaxError` has been deprecated. Use `isSuccess`, `isInvalid` or `handleResponse` instead.", false, {
            id: "ds.adapter.ajax-error-deprecated",
            until: "2.0.0"
          });
          error = adapter.ajaxError(jqXHR, textStatus, errorThrown);
        }

        if (!(error instanceof Error)) {
          if (errorThrown instanceof Error) {
            error = errorThrown;
          } else if (textStatus === "timeout") {
            error = new TimeoutError();
          } else if (textStatus === "abort") {
            error = new AbortError();
          } else {
            error = adapter.handleResponse(jqXHR.status, parseResponseHeaders(jqXHR.getAllResponseHeaders()), adapter.parseErrorResponse(jqXHR.responseText) || errorThrown);
          }
        }

        Ember.run(null, reject, error);
      };

      Ember.$.ajax(hash);
    }, "DS: RESTAdapter#ajax " + type + " to " + url);
  },

  /**
    @method ajaxOptions
    @private
    @param {String} url
    @param {String} type The request type GET, POST, PUT, DELETE etc.
    @param {Object} options
    @return {Object}
  */
  ajaxOptions: function (url, type, options) {
    var hash = options || {};
    hash.url = url;
    hash.type = type;
    hash.dataType = "json";
    hash.context = this;

    if (hash.data && type !== "GET") {
      hash.contentType = "application/json; charset=utf-8";
      hash.data = JSON.stringify(hash.data);
    }

    var headers = get(this, "headers");
    if (headers !== undefined) {
      hash.beforeSend = function (xhr) {
        forEach.call(keysFunc(headers), function (key) {
          xhr.setRequestHeader(key, headers[key]);
        });
      };
    }

    return hash;
  },

  /**
    @method parseErrorResponse
    @private
    @param {String} responseText
    @return {Object}
  */
  parseErrorResponse: function (responseText) {
    var json = responseText;

    try {
      json = Ember.$.parseJSON(responseText);
    } catch (e) {}

    return json;
  },

  /**
    @method normalizeErrorResponse
    @private
    @param  {Number} status
    @param  {Object} headers
    @param  {Object} payload
    @return {Object} errors payload
  */
  normalizeErrorResponse: function (status, headers, payload) {
    if (payload && typeof payload === "object" && payload.errors) {
      return payload.errors;
    } else {
      return [{
        status: "" + status,
        title: "The backend responded with an error",
        detail: "" + payload
      }];
    }
  }
});

function parseResponseHeaders(headerStr) {
  var headers = create(null);
  if (!headerStr) {
    return headers;
  }

  var headerPairs = headerStr.split("\r\n");
  for (var i = 0; i < headerPairs.length; i++) {
    var headerPair = headerPairs[i];
    // Can't use split() here because it does the wrong thing
    // if the header value has the string ": " in it.
    var index = headerPair.indexOf(": ");
    if (index > 0) {
      var key = headerPair.substring(0, index);
      var val = headerPair.substring(index + 2);
      headers[key] = val;
    }
  }

  return headers;
}

//From http://stackoverflow.com/questions/280634/endswith-in-javascript
function endsWith(string, suffix) {
  if (typeof String.prototype.endsWith !== "function") {
    return string.indexOf(suffix, string.length - suffix.length) !== -1;
  } else {
    return string.endsWith(suffix);
  }
}

if (Ember.platform.hasPropertyAccessors) {
  Ember.defineProperty(RestAdapter.prototype, "maxUrlLength", {
    enumerable: false,
    get: function () {
      Ember.deprecate("maxUrlLength has been deprecated (wrong casing). You should use maxURLLength instead.", false, {
        id: "ds.adapter.max-url-length-deprecated-case",
        until: "2.0.0"
      });
      return this.maxURLLength;
    },

    set: function (value) {
      Ember.deprecate("maxUrlLength has been deprecated (wrong casing). You should use maxURLLength instead.", false, {
        id: "ds.adapter.max-url-length-deprecated-case",
        until: "2.0.0"
      });
      set(this, "maxURLLength", value);
    }
  });
}

export default RestAdapter;